'use client';

import Loader from '@/components/ui/loader';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@heidi/design-system/form';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyP,
  typographyVariant,
} from '@heidi/design-system/typography';
import { ArrowLeft } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import {
  TEAM_TRIAL_UPGRADE_REQUEST_MESSAGE_MAX_LENGTH,
  teamTrialUpgradeRequestModalMessages,
} from '@heidi/commerce';
import { useTeamTrialUpgradeRequestModal } from './useTeamTrialUpgradeRequestModal';

export type { TeamTrialUpgradeRequestPlan } from './atoms';

/**
 * Lets non-admin team members compose a short message and signal they want their
 * admin to upgrade. Single app-wide mount in `GlobalModalsWrapper`; callers open via
 * `openTeamTrialUpgradeRequestModalAtom` from `./atoms`.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10069-45867
 */
export function TeamTrialUpgradeRequestModal() {
  const {
    open,
    form,
    messagePlaceholder,
    tierState,
    sendVariant,
    titleMessage,
    descriptionMessage,
    handleDialogOpenChange,
    handleBackOrCancel,
    onSubmit,
    isRequestingTeamPlanUpgrade,
    backAriaLabel,
  } = useTeamTrialUpgradeRequestModal();

  return (
    <DialogDrawer open={open} onOpenChange={handleDialogOpenChange}>
      <DialogDrawerContent
        overlayClassName="z-[100]"
        className="z-[101] flex w-full max-w-[470px] flex-col gap-0 border-border bg-surface p-0 pb-8 shadow-lg sm:rounded-2xl"
        data-testid="team-trial-upgrade-request-modal"
        hideCloseIcon
      >
        <Button
          type="button"
          variant="ghost"
          size="md"
          iconOnly
          aria-label={backAriaLabel}
          onClick={handleBackOrCancel}
          disabled={isRequestingTeamPlanUpgrade}
          className="absolute start-1.5 top-1.5 z-10 shrink-0 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
        >
          <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden />
        </Button>

        <div className="relative flex w-full flex-col items-center px-8">
          <div className="mt-8 flex w-full flex-col items-center gap-6">
            <FramedIcon state={tierState} />

            <div className="flex w-full flex-col items-center gap-4 text-center">
              <DialogDrawerTitle
                className={cn(
                  typographyVariant({ variant: 'h2' }),
                  'text-balance text-center font-exposure text-text-primary'
                )}
              >
                <FormattedMessage {...titleMessage} />
              </DialogDrawerTitle>

              <DialogDrawerDescription asChild>
                <div className="flex w-full flex-col gap-2 text-center">
                  <TypographyP className="text-text-secondary">
                    <FormattedMessage {...descriptionMessage} />
                  </TypographyP>
                  <TypographyCaption className="text-text-secondary">
                    <FormattedMessage
                      {...teamTrialUpgradeRequestModalMessages.evidenceTagline}
                    />
                  </TypographyCaption>
                </div>
              </DialogDrawerDescription>
            </div>

            <Form {...form}>
              <form
                className="flex w-full flex-col gap-6"
                onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              >
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="gap-1.5">
                      <FormLabel className="text-start text-sm font-medium text-text-primary">
                        <FormattedMessage
                          {...teamTrialUpgradeRequestModalMessages.messageLabel}
                        />
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={messagePlaceholder}
                          rows={5}
                          maxLength={TEAM_TRIAL_UPGRADE_REQUEST_MESSAGE_MAX_LENGTH}
                          className="min-h-[120px] resize-y"
                          disabled={isRequestingTeamPlanUpgrade}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <div className="flex items-start justify-between gap-2">
                        <FormMessage className="min-w-0 flex-1" />
                        <TypographyCaption className="ms-auto shrink-0 text-text-tertiary">
                          {(field.value ?? '').length}/
                          {TEAM_TRIAL_UPGRADE_REQUEST_MESSAGE_MAX_LENGTH}
                        </TypographyCaption>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <Button
                    type="submit"
                    variant={sendVariant}
                    size="lg"
                    className="w-full"
                    disabled={isRequestingTeamPlanUpgrade}
                  >
                    {isRequestingTeamPlanUpgrade ? (
                      <Loader
                        className="me-3 shrink-0 text-text-primary-invert"
                        size={22}
                      />
                    ) : (
                      <FormattedMessage {...teamTrialUpgradeRequestModalMessages.send} />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    disabled={isRequestingTeamPlanUpgrade}
                    onClick={handleBackOrCancel}
                  >
                    <FormattedMessage {...teamTrialUpgradeRequestModalMessages.cancel} />
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
